// console.log('[RebuyUtils] File loading started');
window.RebuyUtils = window.RebuyUtils || {};
// console.log('[RebuyUtils] Object created');

window.RebuyUtils = {
  // Fetch the latest cart data from Rebuy
  fetchCart: async ({ useRebuy = true } = {}) => {
    try {
      if (useRebuy && window.Rebuy?.Cart && typeof window.Rebuy.Cart.getCart === 'function') {
        return await window.Rebuy.Cart.getCart();
      }

      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
        cache: 'no-store',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Shopify cart fallback failed with status ${response.status}`);
      }

      const cart = await response.json();

      if (useRebuy && window.Rebuy) {
        window.Rebuy.Cart = window.Rebuy.Cart || {};
        window.Rebuy.Cart.cart = cart;
      }

      return cart;
    } catch (error) {
      console.error('Cart fetch error:', error);
      return null;
    }
  },

  detectAutomaticBuyXGetY: function(cart) {
    if (!cart || !cart.items) return { isActive: false, freeItems: [] };

    const automaticFreeItems = [];
    const automaticDiscountData = [];

    cart.items.forEach(item => {
      // Check for automatic discount indicators
      const hasDiscountedPrice = item.discounted_price !== undefined;
      const isAutomaticFree = hasDiscountedPrice && parseInt(item.discounted_price) === 0;
      const hasLineDiscountAllocations = item.line_level_discount_allocations &&
                                       item.line_level_discount_allocations.length > 0;

      if (isAutomaticFree && hasLineDiscountAllocations) {
        // A stacked line can have more than one allocation. Shopify normally
        // labels automatic allocations with type "automatic"; in this cart
        // response it omits the type, so the configured automatic-code title
        // is the safe fallback. A URL discount_code can never match unless it
        // has explicitly been configured as an automatic campaign code.
        const automaticCodes = [
          ...(window.activeMultiBuyCodes?.autoThreeForTwoCodes || []),
          ...(window.activeMultiBuyCodes?.autoFourForThreeCodes || []),
        ]
          .map(code => String(code || '').trim().toUpperCase())
          .filter(Boolean);
        const allocation = item.line_level_discount_allocations.find(candidate => {
          const discountApp = candidate.discount_application;
          const applicationType = String(discountApp?.type || '').toLowerCase();
          const title = String(discountApp?.title || '').trim().toUpperCase();
          const isConfiguredAutomaticTitle =
            applicationType === '' && automaticCodes.includes(title);
          return (
            discountApp &&
            (applicationType === 'automatic' || isConfiguredAutomaticTitle) &&
            parseFloat(discountApp.value) === 100 &&
            discountApp.value_type === 'percentage'
          );
        });
        const discountApp = allocation?.discount_application;
        const isFullDiscount = Boolean(allocation && discountApp);

        if (isFullDiscount) {
          automaticFreeItems.push({
            id: item.id,
            key: item.key,
            freeQty: item.quantity,
            totalQty: item.quantity,
            price: parseFloat(item.price) / 100,
            title: item.product?.title || 'Item',
            discountAmount: allocation.amount,
            isAutomatic: true
          });

          automaticDiscountData.push({
            itemId: item.id,
            itemKey: item.key,
            item,
            allocation: allocation,
            discountApplication: discountApp
          });
        }
      }
    });

    return {
      isActive: automaticFreeItems.length > 0,
      freeItems: automaticFreeItems,
      discountData: automaticDiscountData,
      totalAutomaticDiscount: cart.total_discount || 0
    };
  },

  // Shopify cart-line keys remain unique when an automatic discount splits one
  // variant into paid and free lines. Variant IDs are retained only for legacy
  // unsplit carts where Rebuy does not expose a cart-line key.
  getCartItemIdentity: function(item) {
    return item?.key || item?.id;
  },

  // URL/UTM codes are checkout-level discounts; a matching code must not be
  // mistaken for one entered directly into Rebuy.
  getDiscountCodeFromCookie: function () {
    const cookies = document.cookie.split(';');
    const discountCookie = cookies.find(cookie => cookie.trim().startsWith('discount_code='));
    if (!discountCookie) return null;

    const code = discountCookie.split('=')[1];
    return code && code !== 'undefined' ? code.trim().toUpperCase() : null;
  },

  // Rebuy's rendered footer is the authoritative reduction when Shopify also
  // owns an automatic Buy X Get Y line.
  getRenderedDiscountAmount: function () {
    const amountElement = document.querySelector('.rebuy-cart__discount-amount > span');
    if (!amountElement || amountElement.offsetParent === null) return null;

    const amountText = amountElement.textContent.trim();
    if (!amountText) return null;

    const parsedAmount = this.parsePriceForLocale(amountText);
    return Number.isFinite(parsedAmount) ? Math.abs(parsedAmount) : 0;
  },

  // Find the discount code applied to the cart
  getActiveDiscount: function () {
    // Helper function for MOV validation - checks for Rebuy error message in the cart, since Rebuy does the validation itself accurately / reliably. No point repeating that particular logic
    function isDiscountActuallyValid() {
      const errorElement = document.querySelector('.rebuy-cart__discount-error-message');
      const hasError = errorElement && errorElement.offsetParent !== null;
      return !hasError;
    }

    function normaliseCode(code) {
      return typeof code === 'string' && code.trim() ? code.trim().toUpperCase() : null;
    }

    const urlDiscountCode = this.getDiscountCodeFromCookie();

    // Priority 1: Rebuy's direct manual discount state.
    const rebuyDiscount = window.Rebuy?.SmartCart?.discount?.discount?.value;
    const rebuyCode = normaliseCode(window.Rebuy?.SmartCart?.discount?.discount?.code);
    const rebuyType = window.Rebuy?.SmartCart?.discount?.discount?.value_type;

    if (typeof rebuyDiscount !== 'undefined' && rebuyCode && rebuyCode !== urlDiscountCode) {
      const parsedValue = parseFloat(rebuyDiscount);
      const resolvedRebuyCode = rebuyCode;

      // NEW: Check if this is a valid 0% gift code
      const isGiftCode =
        resolvedRebuyCode &&
        window.FreeGiftHandler &&
        window.FreeGiftHandler.isValidGiftCode(resolvedRebuyCode) &&
        parsedValue === 0;

      if (isGiftCode) {
        const giftDiscountData = {
          value: 0,
          type: 'free_gift',
          source: 'manual_rebuy',
          code: resolvedRebuyCode,
        };

        // Discount error check
        if (!isDiscountActuallyValid()) {
          console.log('1 [RebuyUtils] Rebuy gift code error detected - invalid');
          return {
            value: 0,
            type: 'free_gift',
            source: 'manual_rebuy',
            code: resolvedRebuyCode,
            isValid: false,
          };
        }

        return giftDiscountData;
      }

      // Otherwise, treat as normal discount
      const normalDiscountData = {
        value: parsedValue,
        type: rebuyType || 'percentage',
        source: 'manual_rebuy',
        code: resolvedRebuyCode,
      };

      // Discount error check
      if (!isDiscountActuallyValid()) {
        console.log('2 [RebuyUtils] Discount invalid - returning invalid object');
        return {
          value: parsedValue,
          type: rebuyType || 'percentage',
          source: 'manual_rebuy',
          code: resolvedRebuyCode,
          isValid: false,
        };
      }
      return normalDiscountData;
    }

    // Priority 2: Shopify checkout-level discounts. The cookie only supplies a
    // missing checkout code; it does not identify a Rebuy-entered code.
    const cartDiscounts = window.Rebuy?.Cart?.cart?.cart_level_discount_applications;
    if (cartDiscounts && cartDiscounts.length > 0) {
      const discountValue = parseFloat(cartDiscounts[0].value);
      const discountType = cartDiscounts[0].value_type;
      let discountCode = cartDiscounts[0].code;

      if (!discountCode || discountCode.trim() === '') {
        discountCode = this.getDiscountCodeFromCookie();
      }

      // Check if this is a 0% free gift code
      const isGiftCode =
        discountCode &&
        window.FreeGiftHandler &&
        window.FreeGiftHandler.isValidGiftCode(discountCode.trim().toUpperCase()) &&
        discountValue === 0;

      if (isGiftCode) {
        const giftDiscountData = {
          value: 0,
          type: 'free_gift',
          source: 'checkout_level',
          code: discountCode.trim().toUpperCase(),
        };

        // Discount error check
        if (!isDiscountActuallyValid()) {
          console.log('3 [RebuyUtils] Discount invalid - no UI updates - return invalid object');
          return {
            value: 0,
            type: 'free_gift',
            source: 'checkout_level',
            code: discountCode.trim().toUpperCase(),
            isValid: false,
          };
        }
        return giftDiscountData;
      }

      const checkoutDiscountData = {
        value: -Math.abs(discountValue),
        type: discountType,
        source: 'checkout_level',
        code: discountCode ? discountCode.trim().toUpperCase() : null,
      };

        // Discount error check
        if (!isDiscountActuallyValid()) {
          console.log('4 [RebuyUtils] Discount invalid - no UI updates - returning invalid object');
          return {
            value: -Math.abs(discountValue),
            type: discountType,
            source: 'checkout_level',
            code: discountCode ? discountCode.trim().toUpperCase() : null,
            isValid: false,
          };
        }

      return checkoutDiscountData;
    }

    // Priority 3: Automatic discounts (Buy X Get Y) - NO MOV validation needed
    const cart = window.Rebuy?.Cart?.cart;
    if (cart) {
      const automaticDiscount = this.detectAutomaticBuyXGetY(cart);
      if (automaticDiscount.isActive) {
        const totalDiscount = parseFloat(cart.total_discount) || 0;

        return {
          value: -Math.abs(totalDiscount),
          type: 'automatic_buyxgety',
          source: 'automatic',
          freeItems: automaticDiscount.freeItems,
          discountData: automaticDiscount.discountData,
        };
      }
    }

    // Default: No discounts
    return {
      value: 0,
      type: 'none',
      source: 'none',
      isValid: true,
      hasDiscount: false,
    };
  },

  parsePriceForLocale: function (priceString) {
    try {
      // Handle both comma and decimal formats
      if (typeof priceString === 'number') return priceString;
      if (typeof priceString !== 'string') return 0;

      // Remove any currency symbols and trim whitespace
      const cleanString = priceString.replace(/[^\d.,]/g, '').trim();

      // Check which decimal separator is used in the string
      const usesCommaDecimal =
        cleanString.indexOf(',') > -1 &&
        (cleanString.indexOf('.') === -1 || cleanString.indexOf(',') > cleanString.indexOf('.'));

      if (usesCommaDecimal) {
        return parseFloat(cleanString.replace(/\./g, '').replace(',', '.'));
      }
      return parseFloat(cleanString.replace(',', ''));
    } catch (error) {
      console.error('Error parsing price:', priceString, error);
      return 0;
    }
  },

  // Proportional discount allocator function
  allocateDiscountAcrossItems: function (cartItems, discountPercentage) {
    // Special case for single item cart - use direct calculation to avoid rounding issues
    if (cartItems.length === 1) {
      const item = cartItems[0];
      const price = this.parsePriceForLocale(item.price) / 100;
      const itemTotal = price * item.quantity;

      // Calculate discount precisely
      const discountAmount = (itemTotal * discountPercentage) / 100;
      const discountedPrice = itemTotal - discountAmount;

      // Create allocation map with single entry
      const allocations = new Map();
      allocations.set(this.getCartItemIdentity(item), {
        originalPrice: itemTotal,
        discountAmount: discountAmount,
        discountedPrice: discountedPrice,
      });

      return {
        allocations,
        totalDiscount: discountAmount,
        discountedTotal: discountedPrice,
      };
    }

    // implementation for multiple items

    // Calculate full subtotal
    const subtotal = cartItems.reduce((sum, item) => {
      const price = this.parsePriceForLocale(item.price) / 100; // Modified line
      return sum + price * item.quantity;
    }, 0);

    // Calculate total discount amount
    const totalDiscountAmount = Math.round(subtotal * discountPercentage) / 100;

    // Calculate discounted total (what Rebuy will show)
    const discountedTotal = subtotal - totalDiscountAmount;

    // Allocate discount proportionally
    const allocations = new Map();
    let allocatedTotal = 0;
    let allocatedDiscount = 0;

    // First pass: allocate proportional discount to each item and track remainders
    const itemsWithRemainders = [];

    cartItems.forEach(item => {
      const price = this.parsePriceForLocale(item.price) / 100;
      const itemTotal = price * item.quantity;
      const proportion = itemTotal / subtotal;

      // Calculate exact discount
      const exactDiscount = totalDiscountAmount * proportion;

      // Round to 2 decimal places
      const roundedDiscount = Math.floor(exactDiscount * 100) / 100;

      // Store remainder for potential adjustment
      const remainder = exactDiscount - roundedDiscount;
      itemsWithRemainders.push({
        item,
        remainder,
        allocatedDiscount: roundedDiscount,
      });

      // Track running totals
      allocatedDiscount += roundedDiscount;

      // Calculate discounted price
      const discountedPrice = itemTotal - roundedDiscount;
      allocatedTotal += discountedPrice;

      // Store allocation
      allocations.set(this.getCartItemIdentity(item), {
        originalPrice: itemTotal,
        discountAmount: roundedDiscount,
        discountedPrice: discountedPrice,
      });
    });

    // Check for rounding error
    const roundingError = totalDiscountAmount - allocatedDiscount;

    if (Math.abs(roundingError) > 0.001) {
      // Sort by remainder (largest first)
      itemsWithRemainders.sort((a, b) => b.remainder - a.remainder);

      // Adjust item with largest remainder
      const adjustItem = itemsWithRemainders[0].item;
      const adjustItemIdentity = this.getCartItemIdentity(adjustItem);
      const currentAllocation = allocations.get(adjustItemIdentity);

      // Apply rounding correction
      const adjustedDiscount = currentAllocation.discountAmount + roundingError;
      const adjustedPrice = currentAllocation.originalPrice - adjustedDiscount;

      // Update allocation
      allocations.set(adjustItemIdentity, {
        originalPrice: currentAllocation.originalPrice,
        discountAmount: adjustedDiscount,
        discountedPrice: adjustedPrice,
      });
    }

    return {
      allocations,
      totalDiscount: totalDiscountAmount,
      discountedTotal: discountedTotal,
    };
  },

  // discount allocation function for FIXED discounts
  allocateFixedDiscountsAcrossItems: function (cartItems, fixedAmount) {
    // Calculate subtotal
    const subtotal = cartItems.reduce((sum, item) => {
      const price = this.parsePriceForLocale(item.price) / 100;
      return sum + price * item.quantity;
    }, 0);

    if (!subtotal || !cartItems.length) {
      return { allocations: new Map(), totalDiscount: 0, discountedTotal: subtotal };
    }

    // The rendered amount cannot reduce paid lines below zero.
    const totalDiscountAmount = Math.min(Math.max(Number(fixedAmount) || 0, 0), subtotal);

    // Calculate discounted total
    const discountedTotal = subtotal - totalDiscountAmount;

    // Allocate discount proportionally across items
    const allocations = new Map();
    let allocatedDiscount = 0;
    const itemsWithRemainders = [];

    cartItems.forEach(item => {
      const price = this.parsePriceForLocale(item.price) / 100;
      const itemTotal = price * item.quantity;
      const proportion = itemTotal / subtotal;

      const exactDiscount = totalDiscountAmount * proportion;
      const itemDiscount = Math.floor(exactDiscount * 100) / 100;
      const discountedPrice = itemTotal - itemDiscount;

      allocatedDiscount += itemDiscount;
      itemsWithRemainders.push({ item, remainder: exactDiscount - itemDiscount });

      allocations.set(this.getCartItemIdentity(item), {
        originalPrice: itemTotal,
        discountAmount: itemDiscount,
        discountedPrice: discountedPrice,
      });
    });

    const roundingError = Math.round((totalDiscountAmount - allocatedDiscount) * 100) / 100;
    if (roundingError > 0 && itemsWithRemainders.length) {
      itemsWithRemainders.sort((a, b) => b.remainder - a.remainder);
      const adjustmentItemIdentity = this.getCartItemIdentity(itemsWithRemainders[0].item);
      const adjustment = allocations.get(adjustmentItemIdentity);
      allocations.set(adjustmentItemIdentity, {
        originalPrice: adjustment.originalPrice,
        discountAmount: adjustment.discountAmount + roundingError,
        discountedPrice: adjustment.discountedPrice - roundingError,
      });
    }

    return {
      allocations,
      totalDiscount: totalDiscountAmount,
      discountedTotal: discountedTotal,
    };
  },

  // Add a gift to the cart
  addGiftToCart: async function (variantId, properties = {}, quantity = 1) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity,
              properties: {
                _free_gift_source: 'bwb_code', // Default ownership for existing callers.
                ...properties,
              },
            },
          ],
        }),
      });
      if (!response.ok) {
        throw new Error(`Gift cart request failed with status ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('[RebuyUtils] Add gift error:', error);
      return null;
    }
  },

  // Remove an item from the cart
  removeItemFromCart: async function (itemKey) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates: { [itemKey]: 0 } }),
      });
      if (!response.ok) {
        throw new Error(`Gift cart request failed with status ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('[RebuyUtils] Remove item error:', error);
      return null;
    }
  },

  // set quantity of an item manually (used to enforce only 1 instance of a free gift)
  setItemQuantity: async function (itemKey, quantity) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates: { [itemKey]: quantity } }),
      });
      if (!response.ok) {
        throw new Error(`Gift cart request failed with status ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('[RebuyUtils] Update item error:', error);
      return null;
    }
  },

  // Remove discount code from Shopify's cart/checkout session
  // Uses Shopify's Cart Ajax API (2024+ native discount support)
  removeDiscountFromCart: async function () {
    try {
      console.log('[RebuyUtils] Removing discount from Shopify cart using Cart Ajax API');

      // Use Shopify's Cart Ajax API to remove discount (May 2024+ feature)
      // Passing empty string as discount parameter removes the discount
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          discount: '' // Empty string removes the discount
        }),
      });

      if (response.ok) {
        const cart = await response.json();
        console.log('[RebuyUtils] ✓ Discount removed from cart:', cart);
        return { success: true, cart: cart };
      }

      console.error('[RebuyUtils] Failed to remove discount, response not OK');
      return { success: false };
    } catch (error) {
      console.error('[RebuyUtils] Remove discount error:', error);
      return { success: false, error: error };
    }
  },
};
